import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Athletes from './pages/Athletes';
import AthleteHome from './pages/AthleteHome';
import NewGame from './pages/NewGame';
import GameDetail from './pages/GameDetail';
import Achievements from './pages/Achievements';
import Paywall from './pages/Paywall';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import Account from './pages/Account';
import Admin from './pages/Admin';
import { Gift, X } from 'lucide-react';
import './App.css';

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return isAuthenticated ? children : <Navigate to="/login" />;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return isAuthenticated ? <Navigate to="/athletes" /> : children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/athletes" element={<ProtectedRoute><Athletes /></ProtectedRoute>} />
      <Route path="/athlete/:athleteId" element={<ProtectedRoute><AthleteHome /></ProtectedRoute>} />
      <Route path="/athlete/:athleteId/new-game" element={<ProtectedRoute><NewGame /></ProtectedRoute>} />
      <Route path="/athlete/:athleteId/game/:gameId" element={<ProtectedRoute><GameDetail /></ProtectedRoute>} />
      <Route path="/athlete/:athleteId/achievements" element={<ProtectedRoute><Achievements /></ProtectedRoute>} />
      <Route path="/paywall" element={<ProtectedRoute><Paywall /></ProtectedRoute>} />
      <Route path="/subscription-success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />
      <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
      <Route path="/admin" element={<Admin />} />
    </Routes>
  );
}

function UpgradeNotificationModal() {
  const { user, dismissUpgradeNotification } = useAuth();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (user?.has_unread_upgrade_notification) {
      setShowModal(true);
    }
  }, [user]);

  const handleDismiss = async () => {
    setShowModal(false);
    await dismissUpgradeNotification();
  };

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[100]">
      <div className="bg-[#1A1A1A] rounded-2xl p-6 w-full max-w-sm text-center relative border border-[#00E5E5]/30">
        <button 
          onClick={handleDismiss}
          className="absolute top-3 right-3 text-gray-400 hover:text-white"
          data-testid="dismiss-upgrade-notification"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="w-16 h-16 bg-[#00E5E5]/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <Gift className="w-8 h-8 text-[#00E5E5]" />
        </div>
        
        <h2 className="text-xl font-bold text-white mb-2">Account Upgraded!</h2>
        
        <p className="text-gray-300 mb-4">
          Great news! Your account has been upgraded to the{' '}
          <span className="text-[#00E5E5] font-semibold">
            {user?.upgrade_notification_plan || 'Premium'}
          </span>{' '}
          plan for free.
        </p>
        
        <p className="text-sm text-gray-400 mb-6">
          Enjoy full access to track your athletes' stats!
        </p>
        
        <button
          onClick={handleDismiss}
          className="w-full bg-[#00E5E5] text-black font-bold py-3 rounded-xl hover:bg-[#5FFBFB] transition-colors"
          data-testid="acknowledge-upgrade-btn"
        >
          Awesome, Thanks!
        </button>
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollToTop />
        <div className="App">
          <Toaster 
            position="top-center" 
            richColors 
            theme="dark"
            toastOptions={{
              style: {
                background: '#1A1A1A',
                border: '1px solid #333',
                color: '#fff'
              }
            }}
          />
          <UpgradeNotificationModal />
          <AppRoutes />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
